import { Badge } from '@ordo/ui/badge'
import { Button } from '@ordo/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@ordo/ui/dialog'
import { Input } from '@ordo/ui/input'
import { Separator } from '@ordo/ui/separator'
import { useContacts, useMergeContacts } from 'hooks/api'
import { useFetchApi } from 'hooks/useFetchApi'
import { GitMerge, Search, X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Contact, ContactDetail } from 'types/contacts'

import {
  AssociationMergeSection,
  buildFieldOverrides,
  collectAssociations,
  contactDisplayName,
  FieldSelector,
  initFieldSelectionsFor,
  MERGE_FIELDS,
  type MergeFieldKey,
} from './merge-shared'

function ContactSearchResult({
  contact,
  onSelect,
  disabled,
}: {
  contact: Contact
  onSelect: () => void
  disabled: boolean
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm hover:bg-muted/50 disabled:opacity-50">
      <div className="min-w-0 flex-1">
        <div className="font-medium">{contactDisplayName(contact)}</div>
        <div className="truncate text-xs text-muted-foreground">
          {[contact.email, contact.organizationName].filter(Boolean).join(' \u2022 ') || 'No details'}
        </div>
      </div>
    </button>
  )
}

export function MergeDialog({ contact, onComplete }: { contact: ContactDetail; onComplete: () => void }) {
  const { fetchApi } = useFetchApi()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [selectedContacts, setSelectedContacts] = useState<Contact[]>([])
  const [sourceDetails, setSourceDetails] = useState<ContactDetail[]>([])
  const [loadingDetails, setLoadingDetails] = useState(false)
  const [step, setStep] = useState<'select' | 'resolve'>('select')
  const [fieldSelections, setFieldSelections] = useState<Record<MergeFieldKey, string>>(
    {} as Record<MergeFieldKey, string>,
  )
  const [excludedAssociationIds, setExcludedAssociationIds] = useState<Set<string>>(new Set())
  // Server-side search — previously this page loaded ALL contacts and filtered
  // in-memory, which OOMed the browser once contact count crossed ~10k.
  const { data: searchResults } = useContacts({ q: search.trim() || undefined, take: 20 })
  const { perform: mergeContacts, loading: merging } = useMergeContacts()
  const searchRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open) {
      setSearch('')
      setSelectedContacts([])
      setSourceDetails([])
      setStep('select')
      setFieldSelections({} as Record<MergeFieldKey, string>)
      setExcludedAssociationIds(new Set())
    }
  }, [open])

  const filteredContacts = useMemo(() => {
    if (!searchResults || !search.trim()) {
      return []
    }
    const excludeIds = new Set([contact.id, ...selectedContacts.map((c) => c.id)])
    return searchResults.filter((c) => !excludeIds.has(c.id)).slice(0, 8)
  }, [searchResults, search, contact.id, selectedContacts])

  const addContact = useCallback((c: Contact) => {
    setSelectedContacts((prev) => [...prev, c])
    setSearch('')
    searchRef.current?.focus()
  }, [])

  const removeContact = useCallback((id: string) => {
    setSelectedContacts((prev) => prev.filter((c) => c.id !== id))
  }, [])

  const goToResolve = useCallback(async () => {
    setFieldSelections(initFieldSelectionsFor(contact.id))
    setExcludedAssociationIds(new Set())
    setLoadingDetails(true)
    try {
      const details = await Promise.all(selectedContacts.map((c) => fetchApi<ContactDetail>(`/api/contacts/${c.id}`)))
      setSourceDetails(details)
    } finally {
      setLoadingDetails(false)
    }
    setStep('resolve')
  }, [contact.id, selectedContacts, fetchApi])

  const allContactsForMerge = useMemo(() => [contact, ...sourceDetails] as ContactDetail[], [contact, sourceDetails])

  const contactsForFieldResolve = useMemo(
    () => (sourceDetails.length > 0 ? [contact, ...sourceDetails] : [contact, ...selectedContacts]),
    [contact, sourceDetails, selectedContacts],
  )

  const allAssociations = useMemo(
    () => allContactsForMerge.flatMap((c) => collectAssociations(c, contactDisplayName(c))),
    [allContactsForMerge],
  )

  const toggleAssociation = useCallback((id: string) => {
    setExcludedAssociationIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  const handleMerge = useCallback(async () => {
    const fieldOverrides = buildFieldOverrides(fieldSelections, contact.id, contactsForFieldResolve)

    await mergeContacts(contact.id, {
      sourceIds: selectedContacts.map((c) => c.id),
      fieldOverrides: Object.keys(fieldOverrides).length > 0 ? fieldOverrides : undefined,
      excludeAssociationIds: excludedAssociationIds.size > 0 ? Array.from(excludedAssociationIds) : undefined,
    })

    setOpen(false)
    onComplete()
  }, [
    contact,
    selectedContacts,
    fieldSelections,
    excludedAssociationIds,
    contactsForFieldResolve,
    mergeContacts,
    onComplete,
  ])

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <GitMerge className="mr-1 h-4 w-4" />
          Merge
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Merge Contacts</DialogTitle>
          <DialogDescription>
            {step === 'select'
              ? 'Select duplicate contacts to merge into this one. The current contact will be kept and the others will be deactivated.'
              : 'Choose which field values and associations to keep for the merged contact.'}
          </DialogDescription>
        </DialogHeader>

        {step === 'select' && (
          <div className="space-y-3">
            <div className="rounded-md border border-border/50 p-3">
              <div className="text-xs font-medium text-muted-foreground">Merging into</div>
              <div className="mt-1 text-sm font-medium">{contactDisplayName(contact)}</div>
              <div className="text-xs text-muted-foreground">
                {[contact.email, contact.organizationName].filter(Boolean).join(' \u2022 ')}
              </div>
            </div>

            {selectedContacts.length > 0 && (
              <div className="space-y-1">
                <div className="text-xs font-medium text-muted-foreground">Selected to merge</div>
                {selectedContacts.map((c) => (
                  <div
                    key={c.id}
                    className="flex items-center justify-between rounded-md border border-border/50 px-3 py-2">
                    <div>
                      <div className="text-sm font-medium">{contactDisplayName(c)}</div>
                      <div className="text-xs text-muted-foreground">
                        {[c.email, c.organizationName].filter(Boolean).join(' \u2022 ')}
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => removeContact(c.id)}>
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <Separator />

            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                ref={searchRef}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search contacts to merge..."
                className="pl-9"
              />
            </div>

            {search.trim() && (
              <div className="max-h-48 overflow-y-auto rounded-md border border-border/50">
                {filteredContacts.length === 0 ? (
                  <div className="px-3 py-4 text-center text-sm text-muted-foreground">No matching contacts</div>
                ) : (
                  filteredContacts.map((c) => (
                    <ContactSearchResult key={c.id} contact={c} onSelect={() => addContact(c)} disabled={false} />
                  ))
                )}
              </div>
            )}
          </div>
        )}

        {step === 'resolve' && (
          <div className="space-y-1">
            <div className="flex flex-wrap gap-1 pb-2">
              {allContactsForMerge.map((c, i) => (
                <Badge key={c.id} variant={i === 0 ? 'default' : 'secondary'}>
                  {contactDisplayName(c)}
                  {i === 0 && ' (target)'}
                </Badge>
              ))}
            </div>

            <Separator />
            <div className="pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Fields</div>
            {MERGE_FIELDS.map((field) => (
              <FieldSelector
                key={field.key}
                field={field}
                contacts={contactsForFieldResolve}
                selected={fieldSelections[field.key] ?? contact.id}
                onSelect={(id) => setFieldSelections((prev) => ({ ...prev, [field.key]: id }))}
              />
            ))}

            <Separator className="my-2" />
            <div className="pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Associations</div>
            {loadingDetails ? (
              <div className="py-2 text-sm text-muted-foreground">Loading associations...</div>
            ) : (
              <AssociationMergeSection
                associations={allAssociations}
                excludedIds={excludedAssociationIds}
                onToggle={toggleAssociation}
              />
            )}
          </div>
        )}

        <DialogFooter>
          {step === 'select' && (
            <Button onClick={goToResolve} disabled={selectedContacts.length === 0 || loadingDetails}>
              {loadingDetails ? 'Loading...' : 'Next: Resolve Fields'}
            </Button>
          )}
          {step === 'resolve' && (
            <>
              <Button variant="outline" onClick={() => setStep('select')} disabled={merging}>
                Back
              </Button>
              <Button onClick={handleMerge} disabled={merging}>
                {merging ? 'Merging...' : `Merge ${allContactsForMerge.length} Contacts`}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
